import React, { useCallback, useState } from 'react';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';

import { trackEvent } from 'src/lib/analytics';
import jira from 'src/lib/api/jira';
import pagerduty from 'src/lib/api/pagerduty';
import { getQueryParams } from 'src/lib/history';

import ErrorMessage from 'src/views/components/ErrorMessage';
import Field from 'src/views/components/Form/Field';
import ModalContainer from 'src/views/components/ModalContainer';
import { PdEscalationPolicySelect, PdUsersSelect } from 'src/views/components/select';

import PdEscalationLevelSelect from './PdEscalationPolicyLevelSelect';

const analyticsEventName = 'sidebar_incidentresponse_reassign';
const ModalReassignIncident = () => {
  const [{ submitting, error }, setState] = useState({});
  const [values, setValues] = useState({ users: null, escalationPolicy: null, level: undefined });

  const closeModal = () => {
    trackEvent(analyticsEventName, { label: 'cancel' });
    jira.closeCurrentModal();
  };

  const onChange = useCallback(({ target: { value, name } }) => {
    setValues(v => ({ ...v, [name]: value }));
  }, []);

  const disabled = {
    users: true,
    escalationPolicy: true,
    level: true,
  };

  let selectedThingName = undefined;
  let actionName = 'Choose at least one target';
  let actionKey = 'choose';

  if (values.users && values.users.length) {
    selectedThingName = 'at least one user';
    disabled.users = false;
    actionName = 'Reassign';
    actionKey = 'reassign';
  }

  if (values.escalationPolicy) {
    selectedThingName = 'Escalation policy';
    disabled.escalationPolicy = false;
    actionName = 'Delegate';
    actionKey = 'delegate';
  }

  if (values.level) {
    selectedThingName = 'Escalation Level';
    disabled.level = false;
    actionName = 'Escalate';
    actionKey = 'escalate';
  }

  if (!selectedThingName) {
    disabled.level = false;
    disabled.escalationPolicy = false;
    disabled.users = false;
  }

  const reassign = useCallback((e) => {
    const { incidentId } = getQueryParams();
    setState({ submitting: true });
    trackEvent(analyticsEventName, { label: actionKey });
    pagerduty
      .reassignIncident(incidentId, values.users, values.escalationPolicy, values.level && values.level.index)
      .then(() => { jira.closeCurrentModal(); })
      .catch(error => setState({ error }));
  }, [values, actionKey]);

  return (
    <ModalContainer title="Reassign incident">
      <div>
        <Field label="Reassign to users">
          <PdUsersSelect
            isDisabled={disabled.users}
            placeholder={disabled.users ? `You can not select users when ${selectedThingName} is selected` : 'Select Users...'}
            value={values.users}
            name="users"
            isMulti
            onChange={onChange}
            className="user-select"
          />
        </Field>
        <Field label="Delegate to escalation policy">
          <PdEscalationPolicySelect
            isClearable
            isDisabled={disabled.escalationPolicy}
            placeholder={disabled.escalationPolicy ? `You can not select Escalation policy when ${selectedThingName} is selected` : 'Select Escalation policy...'}
            value={values.escalationPolicy}
            name="escalationPolicy"
            onChange={onChange}
            className="escalation-policy-select"
          />
        </Field>

        <Field label="Escalate to specific level within current Escalation policy">
          <PdEscalationLevelSelect
            isClearable
            isDisabled={disabled.level}
            placeholder={disabled.level ? `You can not select Escalation policy level when ${selectedThingName} is selected` : 'Select Escalation policy level...'}
            value={values.level}
            name="level"
            onChange={onChange}
            className="escalation-level-select"
          />
        </Field>
        <div style={{ height: '30px' }}/>
        <SectionMessage>
          You can reassign incident to only one of those:
          <ul>
            <li>List of Users</li>
            <li>Escalation policy</li>
            <li>Escalation level of the current policy</li>
          </ul>
        </SectionMessage>

        {error && <ErrorMessage error={['Failed to reassign incident', error]}/>}

        <div style={{ paddingTop: 24, paddingBottom: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonGroup>
            <Button onClick={closeModal}>Cancel</Button>
            <LoadingButton appearance="primary" isLoading={submitting} onClick={reassign} isDisabled={selectedThingName === undefined}>
              {actionName}
            </LoadingButton>
          </ButtonGroup>
        </div>
      </div>
    </ModalContainer>
  );
};

export default ModalReassignIncident;
